import React from 'react';

interface MarkdownParserOptions {
  className?: string;
}

// Simple markdown to JSX parser for policy documents
export function parseMarkdown(markdown: string, options: MarkdownParserOptions = {}): React.ReactElement {
  const lines = markdown.split('\n');
  const elements: React.ReactElement[] = [];
  let inTable = false;
  let tableHeaders: string[] = [];
  let tableRows: string[][] = [];
  let listItems: string[] = [];
  let inList = false;
  let listType: 'ul' | 'ol' = 'ul';

  const flushList = () => {
    if (listItems.length > 0) {
      const ListComponent = listType === 'ol' ? 'ol' : 'ul';
      elements.push(
        <ListComponent key={elements.length} className="list-disc list-inside space-y-2 mb-6 ml-4">
          {listItems.map((item, index) => (
            <li key={index} className="text-gray-700">
              {parseInlineMarkdown(item)}
            </li>
          ))}
        </ListComponent>
      );
      listItems = [];
      inList = false;
    }
  };

  const flushTable = () => {
    if (tableHeaders.length > 0 && tableRows.length > 0) {
      elements.push(
        <div key={elements.length} className="overflow-x-auto mb-6">
          <table className="min-w-full border border-gray-300 bg-white rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                {tableHeaders.map((header, index) => (
                  <th key={index} className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-300">
                    {parseInlineMarkdown(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
                      {parseInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableHeaders = [];
      tableRows = [];
      inTable = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines but flush lists/tables if needed
    if (line === '') {
      if (inList) flushList();
      if (inTable) flushTable();
      continue;
    }

    // Table detection
    if (line.includes('|') && !inList) {
      if (!inTable) {
        // First table row - could be headers
        const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
        
        // Check if next line is a separator
        const nextLine = lines[i + 1]?.trim();
        if (nextLine && nextLine.includes('---')) {
          // This is a header row
          tableHeaders = cells;
          i++; // Skip the separator line
          inTable = true;
          continue;
        } else {
          // Regular table row without headers
          tableRows.push(cells);
          inTable = true;
          continue;
        }
      } else {
        // Continuation of table
        const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');
        tableRows.push(cells);
        continue;
      }
    } else if (inTable) {
      // End of table
      flushTable();
    }

    // List detection
    const listMatch = line.match(/^(\d+\.|\-|\*)\s+(.+)$/);
    if (listMatch) {
      if (!inList) {
        if (inTable) flushTable();
        inList = true;
        listType = listMatch[1].match(/^\d+\./) ? 'ol' : 'ul';
      }
      listItems.push(listMatch[2]);
      continue;
    } else if (inList) {
      flushList();
    }

    // Headers
    if (line.startsWith('# ')) {
      if (inList) flushList();
      if (inTable) flushTable();
      elements.push(
        <h1 key={elements.length} className="text-4xl font-bold text-gray-900 mb-6 mt-8">
          {parseInlineMarkdown(line.substring(2))}
        </h1>
      );
    } else if (line.startsWith('## ')) {
      if (inList) flushList();
      if (inTable) flushTable();
      elements.push(
        <h2 key={elements.length} className="text-3xl font-bold text-gray-900 mb-4 mt-8">
          {parseInlineMarkdown(line.substring(3))}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      if (inList) flushList();
      if (inTable) flushTable();
      elements.push(
        <h3 key={elements.length} className="text-2xl font-semibold text-gray-800 mb-3 mt-6">
          {parseInlineMarkdown(line.substring(4))}
        </h3>
      );
    } else if (line.startsWith('#### ')) {
      if (inList) flushList();
      if (inTable) flushTable();
      elements.push(
        <h4 key={elements.length} className="text-xl font-semibold text-gray-800 mb-2 mt-4">
          {parseInlineMarkdown(line.substring(5))}
        </h4>
      );
    } else if (line.startsWith('---')) {
      // Horizontal rule
      if (inList) flushList();
      if (inTable) flushTable();
      elements.push(
        <hr key={elements.length} className="border-gray-300 my-8" />
      );
    } else {
      // Regular paragraph
      if (inList) flushList();
      if (inTable) flushTable();
      elements.push(
        <p key={elements.length} className="text-gray-700 mb-4 leading-relaxed">
          {parseInlineMarkdown(line)}
        </p>
      );
    }
  }

  // Flush any remaining lists or tables
  if (inList) flushList();
  if (inTable) flushTable();

  return (
    <div className={`prose prose-lg max-w-none ${options.className || ''}`}>
      {elements}
    </div>
  );
}

// Parse inline markdown elements like bold, italic, links
function parseInlineMarkdown(text: string): React.ReactNode {
  const elements: React.ReactNode[] = [];
  let currentText = '';
  let i = 0;

  const flushText = () => {
    if (currentText) {
      elements.push(currentText);
      currentText = '';
    }
  };

  while (i < text.length) {
    const char = text[i];
    const next = text[i + 1];

    // Bold text **text**
    if (char === '*' && next === '*') {
      flushText();
      const endIndex = text.indexOf('**', i + 2);
      if (endIndex !== -1) {
        const boldText = text.substring(i + 2, endIndex);
        elements.push(
          <strong key={elements.length} className="font-bold">
            {boldText}
          </strong>
        );
        i = endIndex + 2;
        continue;
      }
    }

    // Links [text](url)
    if (char === '[') {
      const closeIndex = text.indexOf(']', i);
      const openParen = text.indexOf('(', closeIndex);
      const closeParen = text.indexOf(')', openParen);
      
      if (closeIndex !== -1 && openParen === closeIndex + 1 && closeParen !== -1) {
        flushText();
        const linkText = text.substring(i + 1, closeIndex);
        const linkUrl = text.substring(openParen + 1, closeParen);
        
        elements.push(
          <a
            key={elements.length}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {linkText}
          </a>
        );
        i = closeParen + 1;
        continue;
      }
    }

    currentText += char;
    i++;
  }

  flushText();
  return elements.length === 1 && typeof elements[0] === 'string' ? elements[0] : elements;
}